import { map, tap, catchError, of } from 'rxjs';
import { TermsOfUseService } from './../services/terms-of-use.service';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService } from '../../auth/services/auth/auth.service';
import { ROLES } from '../../permissions/constants/roles';
import { JwtService } from '../../auth/services/jwt/jwt.service';

@Injectable({
    providedIn: 'root'
})
export class TermsOfUseGuard {

    constructor(
        private termsOfUseService: TermsOfUseService,
        private router: Router,
        private authService: AuthService,
        private jwtService: JwtService
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivateRouteOrChild(route, state);
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivateRouteOrChild(childRoute, state);
    }

    private canActivateRouteOrChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        // Skip terms check if we're already on the terms page or navigating away from it
        if (state.url === '/tos' || (state.url !== '/tos' && this.router.url === '/tos')) {
            return true;
        }

        // Check if user is authenticated
        const tokenPair = this.jwtService.getToken();
        if (!tokenPair || this.jwtService.isTokenExpired(tokenPair.accessToken)) {
            // If not authenticated, allow navigation (auth guard will handle redirection if needed)
            return true;
        }

        const termsOfUseResult = this.termsOfUseService.getTermsOfUse();

        return termsOfUseResult.fullResult$.pipe(
            map(response => {
                if (!response || !response.value || response.isFailure) {
                    return true; // Allow navigation if we can't determine terms status
                }
                return !response.value.showTermsOfUseOnLogin;
            }),
            tap(dontShowTermsOfUse => {
                if (!dontShowTermsOfUse) {
                    const showLightWeightTerms = this.authService.hasRole([ROLES.Stakeholder, ROLES.Immigration, ROLES.Tax, ROLES.Collaborator, ROLES.Supplier]);
                    const parsedUrl = new URL(window.location.href);
                    const returnUrl = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;

                    let queryParams: any = {};

                    if (returnUrl) {
                        queryParams.returnUrl = returnUrl;
                    }

                    if (showLightWeightTerms) {
                        queryParams.lightWeightTerms = true;
                    }

                    this.router.navigate(['/tos'], { queryParams });
                }
            }),
            catchError(error => {
                // If we get a 401, allow navigation (auth guard will handle redirection)
                if (error.status === 401) {
                    return of(true);
                }
                // For other errors, allow navigation but log the error
                console.error('Error checking terms of use:', error);
                return of(true);
            })
        );
    }
}
